import { compact } from 'lodash'
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { SQLiteFactory } from '../../models/SQLiteFactory'
import { User } from '../../models/User'
import { useNotificationListener } from '../../providers/NotificationsManagerProvider'
import { PSApplication } from './PSApplication'
import { NavLink } from './design/NavLink'
import NavigationPage from './design/NavigationPage'

type Props = {
    shouldDisableOutletLayout?: boolean
}

const NavigationRoot = ({ shouldDisableOutletLayout = false }: Props) => {
    const user = PSApplication.me()
    const [unreadCount, setUnreadCount] = useState(0)
    const [reportsEnabled, setReportsEnabled] = useState(false)
    const navigate = usePSNavigate()
    const location = useLocation()

    const getUnreadCount = async () => {
        setUnreadCount((await SQLiteFactory.getInstance().badgeCount()) || 0)
    }

    useEffect(() => {
        ;(async () => {
            if (PSApplication.role() >= User.PERM_ROLE_OWNER) {
                setReportsEnabled(true)
            }
            else if (user.payee) {
                const payee = await RecordFactory.getInstance().fetchPayee(user.id ?? '', false)
                setReportsEnabled(!!payee?.showIncome)
            }
            else {
                setReportsEnabled(false)
            }
        })()
    }, [user])

    useEffect(() => {
        if (
            user.plan === 'franchise' &&
            !location.pathname.startsWith('/reports') &&
            !location.pathname.startsWith('/signup')
        ) {
            return navigate('/reports')
        }
        getUnreadCount()
    }, [user?.plan, user?.role, navigate, location.pathname])

    useNotificationListener('MessagesTabDataSourceChanged', getUnreadCount)

    const disabled = user.requiresUpgrade
    let links: NavLink[] = compact([
        { to: '/', label: 'Home', icon: 'home.large' },
        { to: '/clients', label: 'Clients', icon: 'user3.large', disabled, exact: true },
        {
            to: '/messages',
            label: 'Messages',
            icon: 'message.large',
            disabled,
            count: unreadCount === 0 ? undefined : unreadCount,
        },
        { to: '/calendar', label: 'Calendar', icon: 'calendar.large', disabled },
        reportsEnabled ? { to: '/reports', label: 'Reports', icon: 'graph.large', disabled } : null,
        { to: '/settings', label: 'Settings', icon: 'cog.large', disabled },
    ])

    if (user.plan === 'franchise') {
        links = [{ to: '/reports', label: 'Reports', icon: 'graph.large', disabled }]
    }

    return (
        <NavigationPage links={links} user={user} shouldDisableLayout={shouldDisableOutletLayout}>
            <Outlet />
        </NavigationPage>
    )
}

export default NavigationRoot
